import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, TrendingUp, MapPin, Check, ArrowLeft, Calendar, Users, Star, Hotel, Utensils, Dumbbell } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageDetailPage = () => {
  const { packageId } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        const pkg = response.data.find(p => p.id === packageId);
        setPackageData(pkg);
      } catch (error) {
        console.error('Error fetching package:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackage();
  }, [packageId]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  // Hotel options
  const hotels = [
    {
      name: 'Tivoli Alvor Algarve',
      stars: 5,
      type: 'All-Inclusive',
      description: 'Tivoli Alvor Algarve is a five-star all-inclusive resort offering a relaxed and high-quality holiday experience. It features 491 modern rooms and suites, five restaurants and two bars with international cuisine, and six swimming pools alongside a wellness centre, sports facilities and kids\' entertainment. Just a 5-minute walk from Alvor Beach and close to the town centre, the resort is ideal for enjoying the Algarve with comfort and ease.',
      images: [
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/abjzcszq_tivoli%201.avif',
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/f4un8it6_tivoli%202.avif',
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/jzsxcmn0_tivoli3.webp',
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/ghy5z102_tivoli4.webp',
      ],
    },
    {
      name: 'RR Alvor Baía Resort',
      stars: 4,
      type: 'Breakfast Included',
      description: 'Stay at RR Alvor Baía Resort, a 4-star family-friendly retreat in Alvor. Relax by the 100m-long outdoor pool, explore the kids\' pirate ship, or unwind at the 5 Senses Spa with gym, sauna, Turkish bath, and massages. Spacious suites with terraces or balconies offer garden or pool views, and the beach is just a 10-minute walk away. This package includes breakfast only, giving you the freedom to explore and enjoy the best of Portuguese gastronomy.',
      images: [
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/nxqasaqy_baia1.avif',
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/cfrmlnkt_baia2.avif',
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/rg4ta6tv_baia3.avif',
        'https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/pwudixoe_baia4.avif',
      ],
    },
  ];

  // Pricing packs based on package type
  const getPricingPacks = (pkgId) => {
    const basePrices = {
      'weekend-escape': { training1p: 650, training2p: 450, bb1p: 'request', bb2p: 'request', ai1p: 'request', ai2p: 'request', trainingHours: 9, trainingType: 'Individual' },
      'classic-week': { training1p: 820, training2p: 640, bb1p: 'request', bb2p: 'request', ai1p: 'request', ai2p: 'request', trainingHours: 14, trainingType: 'Individual' },
      'intensive-camp': { training1p: 450, training2p: null, bb1p: 'request', bb2p: 'request', ai1p: 'request', ai2p: 'request', trainingHours: 14, trainingType: 'Group' },
      'corporate-retreat': { training1p: 'request', training2p: 'request', bb1p: 'request', bb2p: 'request', ai1p: 'request', ai2p: 'request', trainingHours: 20, trainingType: 'Group' },
    };
    return basePrices[pkgId] || basePrices['classic-week'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f3ee] flex items-center justify-center pt-24">
        <div className="text-[#414b3b]/50">Loading...</div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen bg-[#f5f3ee] flex items-center justify-center pt-24">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-[#414b3b] mb-4">Package not found</h2>
          <Link to="/experiences" className="text-[#8ca87c] hover:underline">
            ← Back to Experiences
          </Link>
        </div>
      </div>
    );
  }

  const itinerary = {
    'weekend-escape': [
      { day: 'Day 1', title: 'Arrival & Welcome', activities: ['Airport pickup & transfer to Alvor', 'Check-in & settle in', 'Welcome dinner with the group', 'Meet your coaches and fellow players'] },
      { day: 'Day 2', title: 'Training Day', activities: ['Morning coaching session', 'Technical & tactical training', 'Lunch break', 'Afternoon matches with level-matched players'] },
      { day: 'Day 3', title: 'Intensive Training', activities: ['Morning individual training', 'Competition methodology', 'Lunch at the club', 'Friendly tournament with local players'] },
      { day: 'Day 4', title: 'Final Day & Departure', activities: ['Morning padel session', 'Brunch & farewell', 'Transfer to airport', 'Departure'] },
    ],
    'classic-week': [
      { day: 'Day 1', title: 'Arrival & Welcome', activities: ['Airport pickup', 'Hotel check-in', 'Welcome drinks & dinner', 'Group introduction'] },
      { day: 'Day 2', title: 'Intensive Training', activities: ['Morning coaching clinics', 'Technique workshops', 'Afternoon competitive matches', 'Evening social activities'] },
      { day: 'Day 3', title: 'Training & Relaxation', activities: ['Morning individual training', 'Lunch at the club', 'Afternoon beach time', 'Optional evening padel'] },
      { day: 'Day 4', title: 'Tournament Day', activities: ['Tournament preparation', 'Friendly tournament', 'Awards & celebration', 'Farewell dinner'] },
      { day: 'Day 5', title: 'Departure', activities: ['Morning free time', 'Farewell brunch', 'Airport transfer', 'Departure'] },
    ],
    'intensive-camp': [
      { day: 'Day 1', title: 'Assessment Day', activities: ['Arrival & check-in', 'Initial skill assessment', 'Video analysis setup', 'Welcome dinner'] },
      { day: 'Day 2-3', title: 'Technical Focus', activities: ['Intensive morning drills', 'Private coaching sessions', 'Footwork & positioning', 'Match play practice'] },
      { day: 'Day 4', title: 'Recovery & Strategy', activities: ['Spa & recovery session', 'Video review', 'Tactical workshops', 'Light evening session'] },
      { day: 'Day 5', title: 'Competition & Farewell', activities: ['Final tournament', 'Awards ceremony', 'Farewell lunch', 'Departure'] },
    ],
    'corporate-retreat': [
      { day: 'Day 1', title: 'Team Arrival', activities: ['Group airport transfer', 'Team check-in', 'Ice-breaker padel session', 'Welcome gala dinner'] },
      { day: 'Day 2', title: 'Team Building', activities: ['Padel workshops for all levels', 'Team tournament', 'Conference facilities available', 'Group dinner'] },
      { day: 'Day 3', title: 'Training & Strategy', activities: ['Morning coaching clinics', 'Team strategy sessions', 'Afternoon matches', 'Evening social activities'] },
      { day: 'Day 4', title: 'Relaxation Day', activities: ['Beach excursion', 'Optional padel session', 'Spa & wellness time', 'Team dinner'] },
      { day: 'Day 5', title: 'Competition Day', activities: ['Tournament preparation', 'Inter-team competition', 'Awards ceremony', 'Celebration dinner'] },
      { day: 'Day 6', title: 'Culture & Exploration', activities: ['Local sightseeing tour', 'Wine tasting experience', 'Free time', 'Farewell gala dinner'] },
      { day: 'Day 7', title: 'Departure', activities: ['Morning free time', 'Team photos', 'Farewell brunch', 'Airport transfer & departure'] },
    ],
  };

  const packageItinerary = itinerary[packageId] || itinerary['classic-week'];
  const prices = getPricingPacks(packageId);

  return (
    <div className="bg-[#f5f3ee]" data-testid="package-detail-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-end overflow-hidden" data-testid="package-hero">
        <div className="absolute inset-0">
          <img
            src={packageData.image_url}
            alt={packageData.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16 w-full">
          <Link
            to="/experiences"
            className="inline-flex items-center gap-2 text-[#f5f3ee]/70 hover:text-[#f5f3ee] mb-8 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={18} />
            <span className="text-sm tracking-wider uppercase">Back to Experiences</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading font-light text-4xl sm:text-5xl md:text-6xl text-[#f5f3ee] mb-4">
              {packageData.name}
            </h1>
            
            {/* Simple info line */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[#f5f3ee]/70 text-sm tracking-wider">
              <span>{packageData.destination}</span>
              <span className="hidden sm:inline">•</span>
              <span>{packageData.duration}</span>
              <span className="hidden sm:inline">•</span>
              <span>{packageData.level}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Packs Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="pricing-packs">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp}>
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Choose Your Pack
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-4">
              Pricing Options
            </h2>
            <p className="text-[#414b3b]/60 font-light mb-12 max-w-2xl">
              Select the pack that best suits your needs. All packs include professional coaching and equipment.
            </p>
          </motion.div>

          {/* Pricing Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="pricing-table">
              <thead>
                <tr className="border-b-2 border-[#414b3b]/10">
                  <th className="text-left py-4 px-4 font-heading text-lg text-[#414b3b]">Pack</th>
                  <th className="text-left py-4 px-4 font-heading text-lg text-[#414b3b]">What's Included</th>
                  <th className="text-center py-4 px-4 font-heading text-lg text-[#414b3b]">1 Person</th>
                  <th className="text-center py-4 px-4 font-heading text-lg text-[#414b3b]">2 Persons</th>
                </tr>
              </thead>
              <tbody>
                {/* Pack 1 - Training Only */}
                <tr className="border-b border-[#414b3b]/10 hover:bg-[#f5f3ee]/50 transition-colors">
                  <td className="py-6 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#8ca87c]/10 flex items-center justify-center rounded-full">
                        <Dumbbell size={18} className="text-[#8ca87c]" />
                      </div>
                      <div>
                        <span className="font-heading text-lg text-[#414b3b] block">Pack 1</span>
                        <span className="text-sm text-[#414b3b]/60">Training Only</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-6 px-4">
                    <ul className="space-y-1 text-sm text-[#414b3b]/70">
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> {prices.trainingHours || 14} hours of {prices.trainingType || 'Individual'} Padel training</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Technical & tactical training</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Competition methodology</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Matches & friendly tournament</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Welcome package (academy t-shirt)</li>
                    </ul>
                  </td>
                  <td className="py-6 px-4 text-center">
                    {prices.training1p === 'request' ? (
                      <>
                        <span className="font-heading text-lg text-[#414b3b]">Under</span>
                        <span className="text-sm text-[#414b3b]/50 block">Request</span>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-2xl text-[#414b3b]">€{prices.training1p}</span>
                        <span className="text-sm text-[#414b3b]/50 block">per person</span>
                      </>
                    )}
                  </td>
                  <td className="py-6 px-4 text-center">
                    {prices.training2p === 'request' ? (
                      <>
                        <span className="font-heading text-lg text-[#8ca87c]">Under</span>
                        <span className="text-sm text-[#414b3b]/50 block">Request</span>
                      </>
                    ) : prices.training2p ? (
                      <>
                        <span className="font-heading text-2xl text-[#8ca87c]">€{prices.training2p}</span>
                        <span className="text-sm text-[#414b3b]/50 block">per person</span>
                      </>
                    ) : (
                      <span className="text-[#414b3b]/30">—</span>
                    )}
                  </td>
                </tr>

                {/* Pack 2 - Training + B&B */}
                <tr className="border-b border-[#414b3b]/10 hover:bg-[#f5f3ee]/50 transition-colors bg-[#f5f3ee]/30">
                  <td className="py-6 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#8ca87c]/10 flex items-center justify-center rounded-full">
                        <Utensils size={18} className="text-[#8ca87c]" />
                      </div>
                      <div>
                        <span className="font-heading text-lg text-[#414b3b] block">Pack 2</span>
                        <span className="text-sm text-[#414b3b]/60">Training + B&B</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-6 px-4">
                    <ul className="space-y-1 text-sm text-[#414b3b]/70">
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> {prices.trainingHours || 14} hours of {prices.trainingType || 'Individual'} Padel training</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Technical & tactical training</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Competition methodology</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Matches & friendly tournament</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Welcome package (academy t-shirt)</li>
                      <li className="flex items-center gap-2 mt-2 pt-2 border-t border-[#414b3b]/10"><Hotel size={14} className="text-[#8ca87c]" /> RR Alvor Baía Resort (4★)</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Breakfast included daily</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Spa & pool access</li>
                    </ul>
                  </td>
                  <td className="py-6 px-4 text-center">
                    {prices.bb1p === 'request' ? (
                      <>
                        <span className="font-heading text-lg text-[#414b3b]">Under</span>
                        <span className="text-sm text-[#414b3b]/50 block">Request</span>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-2xl text-[#414b3b]">€{prices.bb1p}</span>
                        <span className="text-sm text-[#414b3b]/50 block">per person</span>
                      </>
                    )}
                  </td>
                  <td className="py-6 px-4 text-center">
                    {prices.bb2p === 'request' ? (
                      <>
                        <span className="font-heading text-lg text-[#8ca87c]">Under</span>
                        <span className="text-sm text-[#414b3b]/50 block">Request</span>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-2xl text-[#8ca87c]">€{prices.bb2p}</span>
                        <span className="text-sm text-[#414b3b]/50 block">per person</span>
                      </>
                    )}
                  </td>
                </tr>

                {/* Pack 3 - Training + All-Inclusive */}
                <tr className="hover:bg-[#f5f3ee]/50 transition-colors">
                  <td className="py-6 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#8ca87c] flex items-center justify-center rounded-full">
                        <Star size={18} className="text-white" />
                      </div>
                      <div>
                        <span className="font-heading text-lg text-[#414b3b] block">Pack 3</span>
                        <span className="text-sm text-[#8ca87c] font-medium">Premium All-Inclusive</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-6 px-4">
                    <ul className="space-y-1 text-sm text-[#414b3b]/70">
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> {prices.trainingHours || 14} hours of {prices.trainingType || 'Individual'} Padel training</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Technical & tactical training</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Competition methodology</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Matches & friendly tournament</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> Welcome package (academy t-shirt)</li>
                      <li className="flex items-center gap-2 mt-2 pt-2 border-t border-[#414b3b]/10"><Hotel size={14} className="text-[#8ca87c]" /> Tivoli Alvor Algarve (5★)</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> All meals & drinks included</li>
                      <li className="flex items-center gap-2"><Check size={14} className="text-[#8ca87c]" /> 6 pools, wellness centre</li>
                    </ul>
                  </td>
                  <td className="py-6 px-4 text-center">
                    {prices.ai1p === 'request' ? (
                      <>
                        <span className="font-heading text-lg text-[#414b3b]">Under</span>
                        <span className="text-sm text-[#414b3b]/50 block">Request</span>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-2xl text-[#414b3b]">€{prices.ai1p}</span>
                        <span className="text-sm text-[#414b3b]/50 block">per person</span>
                      </>
                    )}
                  </td>
                  <td className="py-6 px-4 text-center">
                    {prices.ai2p === 'request' ? (
                      <>
                        <span className="font-heading text-lg text-[#8ca87c]">Under</span>
                        <span className="text-sm text-[#414b3b]/50 block">Request</span>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-2xl text-[#8ca87c]">€{prices.ai2p}</span>
                        <span className="text-sm text-[#414b3b]/50 block">per person</span>
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-[#414b3b]/50 mt-6 text-center">
            * Prices are per person. Group discounts available for 4+ people. Contact us for custom quotes.
          </p>

          {/* Possible Extras */}
          <div className="mt-16 p-8 bg-[#f5f3ee]">
            <h3 className="font-heading text-xl text-[#414b3b] mb-6 text-center">
              Possible Extras
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="bg-white p-4 text-center">
                <span className="text-[#8ca87c] block mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </span>
                <span className="font-heading text-[#414b3b] block">Airport Transfer</span>
              </div>
              <div className="bg-white p-4 text-center">
                <span className="text-[#8ca87c] block mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21h18M9 8h6m-6 4h6m-6 4h6M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16" />
                  </svg>
                </span>
                <span className="font-heading text-[#414b3b] block">Guided Tours</span>
              </div>
              <div className="bg-white p-4 text-center">
                <span className="text-[#8ca87c] block mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <span className="font-heading text-[#414b3b] block">Extra Padel Hours</span>
              </div>
            </div>
            <p className="text-sm text-[#414b3b]/50 mt-4 text-center">
              Contact us to customize your experience with additional services
            </p>
          </div>
        </div>
      </section>

      {/* Hotel Options */}
      <section className="py-20 md:py-28 bg-[#f5f3ee]" data-testid="hotel-options">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp}>
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Accommodation
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-12">
              Suggested Hotels
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {hotels.map((hotel, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white overflow-hidden"
                data-testid={`hotel-${index}`}
              >
                {/* Image Gallery */}
                <div className="relative">
                  <div className="grid grid-cols-2 gap-1">
                    {hotel.images.slice(0, 4).map((img, imgIndex) => (
                      <div key={imgIndex} className="aspect-[4/3] overflow-hidden">
                        <img
                          src={img}
                          alt={`${hotel.name} ${imgIndex + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1.5 bg-white text-[#414b3b] text-xs tracking-wider uppercase flex items-center gap-1">
                      {[...Array(hotel.stars)].map((_, i) => (
                        <Star key={i} size={12} className="fill-[#8ca87c] text-[#8ca87c]" />
                      ))}
                    </span>
                    <span className="px-3 py-1.5 bg-[#8ca87c] text-white text-xs tracking-wider uppercase">
                      {hotel.type}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-heading text-2xl text-[#414b3b] mb-4">
                    {hotel.name}
                  </h3>
                  <p className="text-[#414b3b]/60 font-light leading-relaxed text-sm">
                    {hotel.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Padel Club Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="padel-club">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp}>
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Where You'll Play
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-12">
              Our Partner Club
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-2"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/2v6nky9j_WhatsApp%20Image%202026-02-07%20at%2012.21.37.jpeg"
                  alt="Riade Clube de Padel"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/lz50elzp_WhatsApp%20Image%202026-02-07%20at%2012.40.46%20%281%29.jpeg"
                  alt="Riade Clube de Padel"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/srobgf4n_WhatsApp%20Image%202026-02-07%20at%2012.40.46.jpeg"
                  alt="Riade Clube de Padel"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/qbrkl5mb_WhatsApp%20Image%202026-02-07%20at%2012.40.45.jpeg"
                  alt="Riade Clube de Padel"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            {/* Club Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:pl-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1.5 bg-[#8ca87c] text-white text-xs tracking-wider uppercase">
                  Partner Club
                </span>
                <span className="px-3 py-1.5 bg-[#f5f3ee] text-[#414b3b] text-xs tracking-wider uppercase">
                  Algarve
                </span>
              </div>
              <h3 className="font-heading text-3xl text-[#414b3b] mb-6">
                Riade Clube de Padel
              </h3>
              <p className="text-[#414b3b]/60 font-light leading-relaxed mb-6">
                Riade Clube de Padel is a centre of excellence dedicated to the practice and promotion of padel, offering modern facilities, a professional environment, and a community passionate about the sport.
              </p>
              <p className="text-[#414b3b]/60 font-light leading-relaxed mb-8">
                Located in a prime area, the club combines competition, leisure, and socialising, providing high-quality experiences for both beginners and experienced players.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#f5f3ee] p-4 text-center">
                  <span className="font-heading text-2xl text-[#8ca87c] block">6+</span>
                  <span className="text-sm text-[#414b3b]/60">Padel Courts</span>
                </div>
                <div className="bg-[#f5f3ee] p-4 text-center">
                  <span className="font-heading text-2xl text-[#8ca87c] block">Pro</span>
                  <span className="text-sm text-[#414b3b]/60">Coaches</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Itinerary */}
      <section className="py-20 md:py-28 bg-white" data-testid="itinerary">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp}>
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Your Journey
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-12">
              Sample Itinerary
            </h2>
          </motion.div>

          <div className="space-y-8">
            {packageItinerary.map((day, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="grid md:grid-cols-12 gap-6 pb-8 border-b border-[#414b3b]/10 last:border-0"
                data-testid={`itinerary-day-${index}`}
              >
                <div className="md:col-span-2">
                  <span className="font-heading text-2xl text-[#8ca87c]">{day.day}</span>
                </div>
                <div className="md:col-span-3">
                  <h3 className="font-heading text-xl text-[#414b3b]">{day.title}</h3>
                </div>
                <div className="md:col-span-7">
                  <ul className="space-y-2">
                    {day.activities.map((activity, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-[#8ca87c] mt-1.5">✦</span>
                        <span className="text-[#414b3b]/70 font-light">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#414b3b]" data-testid="package-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#f5f3ee] mb-6">
              Ready to Book?
            </h2>
            <p className="text-lg text-[#f5f3ee]/60 mb-10 max-w-xl mx-auto font-light">
              Secure your spot on the {packageData.name} experience. 
              Limited availability to ensure a premium experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-14 px-10 bg-[#f5f3ee] text-[#414b3b] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#8ca87c]"
                data-testid="cta-book-btn"
              >
                Book Now
              </Link>
              <Link
                to="/contact"
                className="h-14 px-10 border border-[#f5f3ee]/30 text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center transition-all duration-300 hover:border-[#f5f3ee]"
                data-testid="cta-enquire-btn"
              >
                Enquire
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PackageDetailPage;
